import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { ArrowRight, ExternalLink } from "lucide-react";
import { api } from "@/lib/api";
import { usePageMeta } from "@/hooks/usePageMeta";
import { BRAND, formatDate } from "@/lib/site";
import { AffiliateDisclosure, AFF_REL, pickUrl } from "@/lib/affiliate";

export interface GuideSummary { slug: string; title: string; intro: string; count: number; updated_at: string }
interface GuidePick { id: string; name: string; note: string; store_label: string }
interface GuideFull { slug: string; title: string; intro: string; updated_at: string; picks: GuidePick[] }

export function useGuides() {
  const [guides, setGuides] = useState<GuideSummary[] | null>(null);
  useEffect(() => { api.get("/public/guides").then((r) => setGuides(r.data)).catch(() => setGuides([])); }, []);
  return guides;
}

export function GuideCards({ guides }: { guides: GuideSummary[] }) {
  return (
    <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
      {guides.map((g, i) => (
        <Link key={g.slug} to={`/guides/${g.slug}`} className="calm-card block p-6 fade-up" style={{ animationDelay: `${i * 50}ms` }} data-testid={`guide-card-${g.slug}`}>
          <span className="pill">{g.count} {g.count === 1 ? "pick" : "picks"}</span>
          <span className="mt-3 block font-heading text-xl font-semibold text-[#23324A]">{g.title}</span>
          {g.intro && <span className="mt-2 block line-clamp-2 text-sm text-[#23324A]/70">{g.intro}</span>}
          <span className="mt-4 inline-flex items-center gap-1 text-sm font-semibold text-[#D9785C]">Read the guide <ArrowRight className="h-4 w-4" /></span>
        </Link>
      ))}
    </div>
  );
}

export function GuidesIndexPage() {
  usePageMeta(`Gift guides | ${BRAND}`, "Short, hand-picked gift guides: supplies and gifts Kelly actually uses, sorted by budget and occasion.");
  const guides = useGuides();
  return (
    <div className="mx-auto max-w-6xl px-5 pt-14 sm:px-8" data-testid="guides-page">
      <p className="mb-3 text-xs font-semibold uppercase tracking-[0.2em] text-[#23324A]/55">Gift guides</p>
      <h1 className="font-heading text-4xl font-semibold text-[#23324A] sm:text-5xl lg:text-6xl">Hand-picked gift guides</h1>
      <p className="mt-4 max-w-xl text-[#23324A]/75">Short lists of things I actually use and would give, sorted by budget and occasion. - Kelly</p>
      {guides && guides.length === 0 && <p className="mt-10 text-sm text-[#23324A]/60" data-testid="guides-empty">The first guides are on their way.</p>}
      <div className="mt-10"><GuideCards guides={guides || []} /></div>
    </div>
  );
}

export function GuidePage() {
  const { slug } = useParams();
  const [g, setG] = useState<GuideFull | null | false>(null);
  useEffect(() => { setG(null); api.get(`/public/guides/${slug}`).then((r) => setG(r.data)).catch(() => setG(false)); }, [slug]);
  usePageMeta(g ? `${g.title} | ${BRAND}` : `Gift guides | ${BRAND}`, g ? g.intro.slice(0, 155) : "Hand-picked gift guides.");
  if (g === null) return <p className="mx-auto max-w-3xl px-5 pt-16 text-sm text-[#23324A]/60">Loading…</p>;
  if (g === false) {
    return (
      <div className="mx-auto max-w-3xl px-5 pt-16 sm:px-8" data-testid="guide-not-found">
        <h1 className="font-heading text-4xl font-semibold text-[#23324A]">We couldn't find that guide</h1>
        <Link to="/guides" className="btn-solid mt-6">See all gift guides</Link>
      </div>
    );
  }
  return (
    <article className="mx-auto max-w-3xl px-5 pt-14 sm:px-8" data-testid="guide-page">
      <Link to="/guides" className="text-xs font-semibold uppercase tracking-[0.2em] text-[#23324A]/55 hover:text-[#23324A]">Gift guides</Link>
      <h1 className="mt-3 font-heading text-4xl font-semibold leading-tight text-[#23324A] sm:text-5xl" data-testid="guide-title">{g.title}</h1>
      <p className="mt-3 text-sm text-[#23324A]/60">By Kelly · Updated {formatDate(g.updated_at)}</p>
      <AffiliateDisclosure className="mt-5" />
      {g.intro && g.intro.split(/\n+/).map((t, i) => <p key={i} className="mt-6 text-lg leading-relaxed text-[#23324A]">{t}</p>)}
      <ol className="mt-10 space-y-4" data-testid="guide-picks">
        {g.picks.map((p, i) => (
          <li key={p.id} className="calm-card flex gap-5 p-5" data-testid={`guide-pick-${p.id}`}>
            <span className="w-8 shrink-0 text-center font-heading text-3xl font-semibold tabular-nums text-[#D9785C]">{i + 1}</span>
            <div className="min-w-0 flex-1">
              <p className="font-heading text-xl font-semibold text-[#23324A]">{p.name}</p>
              {p.note && <p className="mt-1 text-[#23324A]/75">{p.note}</p>}
              <a href={pickUrl(p.id, `guide-${g.slug}`)} target="_blank" rel={AFF_REL} className="btn-solid mt-4" data-testid={`guide-pick-link-${p.id}`}>
                View on {p.store_label} <ExternalLink className="h-4 w-4" />
              </a>
            </div>
          </li>
        ))}
      </ol>
      {g.picks.length === 0 && <p className="mt-8 text-sm text-[#23324A]/60">Picks for this guide are coming soon.</p>}
    </article>
  );
}
